import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Clock, FileText, Users, ShoppingBag, Upload, Heart, ImageIcon } from 'lucide-react';

const actionIcons = {
  created_post: FileText,
  liked_post: Heart,
  created_community: Users,
  joined_community: Users,
  listed_product: ShoppingBag,
  uploaded_file: Upload,
  analyzed_image: ImageIcon,
};

const actionLabels = {
  created_post: 'Created a post',
  liked_post: 'Liked a post',
  created_community: 'Created a community',
  joined_community: 'Joined a community',
  listed_product: 'Listed a product',
  uploaded_file: 'Uploaded a file',
  analyzed_image: 'Analyzed an image',
};

export default function ActivityPage() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadActivity(); }, []);

  const loadActivity = async () => {
    try { const res = await api.get('/activity'); setActivities(res.data); }
    catch {}
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="activity-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">Activity</h1>
      <p className="text-sm text-muted-foreground mb-8">Your recent activity history</p>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
      ) : activities.length === 0 ? (
        <div className="text-center py-16 border border-border/40">
          <Clock size={32} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-sm text-muted-foreground">No activity yet. Start creating, posting, and exploring.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {activities.map((a, i) => {
            const Icon = actionIcons[a.action] || Clock;
            return (
              <div
                key={a.id}
                className="flex items-center gap-4 py-4 border-b border-border/20 last:border-0 animate-in"
                style={{ animationDelay: `${i * 30}ms` }}
                data-testid={`activity-${a.id}`}
              >
                <div className="w-8 h-8 bg-mist flex items-center justify-center shrink-0">
                  <Icon size={14} className="text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{actionLabels[a.action] || a.action}</p>
                  <p className="text-[10px] text-muted-foreground">{a.target_type && `${a.target_type}`}</p>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
